/**
 * Modal oscillator — ADR-038 §14 (J4): **one damped spring per surface mode,
 * kicked by velocity changes, stepped exactly, saturated for render.**
 *
 * ONE responsibility: the weight of each mode over time. Each mode k obeys
 *   q̈ = −2 ζ ω q̇ − ω² q,   ω = 2π · freqHz
 * and a kick adds Δv straight to q̇ (the pudding's frame-rate-independent rule,
 * §14.1). The step is the EXACT propagator of that equation over h, not an
 * Euler sub-step: stable for any ω·h (a 20 Hz mode at a 30 fps frame is fine)
 * and the same state at the same time whatever the frame rate — the §14.7
 * frame-rate proof holds by construction. Each mode's envelope falls as
 * e^(−ζωt), so with one ζ the finer (faster) ripples die first (§14.10).
 *
 * Rendering reads `weights()`: q saturated as max · tanh(q / max) so a linear
 * mode can never invert the flesh; the state itself stays linear.
 *
 * ENGINE-FREE (§14.3): numbers in, numbers out — the same code in Harry's
 * preview and in World.
 *
 * Depends on: ir/errors. Depended on by: the region jiggle (app), tests.
 */
import { KernelError } from '../ir/errors.js';

export interface ModeSpring {
  /** Natural frequency, Hz. */
  readonly freqHz: number;
  /** Damping ratio (0 < ζ < 1: it rings; the envelope is e^(−ζωt)). */
  readonly zeta: number;
  /** The render cap for this mode's weight (tanh saturation); 0 = no cap. */
  readonly max: number;
}

export interface ModalOscillator {
  /** Add a velocity change to mode k (Δv into q̇). */
  kick(k: number, dv: number): void;
  /** Advance every mode by `dt` seconds, exactly. */
  step(dt: number): void;
  /** The saturated weights to render, one per mode (a fresh array). */
  weights(): Float64Array;
  /** The raw linear state (tests, probes). */
  state(): { readonly q: Float64Array; readonly qd: Float64Array };
  /** Every mode at rest. */
  reset(): void;
  /** True when every mode's displacement and velocity are below the given thresholds. */
  quiet(q?: number, qd?: number): boolean;
}

export function createModalOscillator(modes: readonly ModeSpring[]): ModalOscillator {
  for (const m of modes) {
    if (!(m.freqHz > 0) || !(m.zeta > 0) || !(m.zeta < 1)) {
      throw new KernelError(`modal oscillator refused: a mode needs freqHz > 0 and 0 < zeta < 1 (got ${m.freqHz} Hz, ζ ${m.zeta})`);
    }
  }
  const n = modes.length;
  const q = new Float64Array(n);
  const qd = new Float64Array(n);
  // The propagator depends only on h; cache the last one (a steady clock asks for the same h).
  let cachedH = NaN;
  const P = new Float64Array(4 * n);
  const propagator = (h: number): void => {
    if (h === cachedH) return;
    cachedH = h;
    for (let k = 0; k < n; k++) {
      const w = 2 * Math.PI * modes[k]!.freqHz;
      const z = modes[k]!.zeta;
      const wd = w * Math.sqrt(1 - z * z);
      const e = Math.exp(-z * w * h);
      const c = Math.cos(wd * h);
      const s = Math.sin(wd * h);
      // x(h) = e[(c + zw/wd s) x0 + (s/wd) v0];  v(h) = e[(−w²/wd s) x0 + (c − zw/wd s) v0]
      P[4 * k] = e * (c + ((z * w) / wd) * s);
      P[4 * k + 1] = e * (s / wd);
      P[4 * k + 2] = e * ((-w * w * s) / wd);
      P[4 * k + 3] = e * (c - ((z * w) / wd) * s);
    }
  };
  return {
    kick(k, dv) {
      qd[k] = qd[k]! + dv;
    },
    step(dt) {
      if (!(dt > 0)) return;
      propagator(dt);
      for (let k = 0; k < n; k++) {
        const x = q[k]!;
        const v = qd[k]!;
        q[k] = P[4 * k]! * x + P[4 * k + 1]! * v;
        qd[k] = P[4 * k + 2]! * x + P[4 * k + 3]! * v;
      }
    },
    weights() {
      return Float64Array.from(q, (x, k) => {
        const m = modes[k]!.max;
        return m > 0 ? m * Math.tanh(x / m) : x;
      });
    },
    state: () => ({ q: Float64Array.from(q), qd: Float64Array.from(qd) }),
    reset() {
      q.fill(0);
      qd.fill(0);
    },
    quiet(qTol = 1e-6, qdTol = 1e-5) {
      for (let k = 0; k < n; k++) if (Math.abs(q[k]!) > qTol || Math.abs(qd[k]!) > qdTol) return false;
      return true;
    },
  };
}

/** ζ that brings a mode at `freqHz` to `fraction` of its amplitude in `seconds` (§14.10's lifespan rule). */
export function zetaForLifespan(freqHz: number, seconds: number, fraction = 0.05): number {
  return -Math.log(fraction) / (seconds * 2 * Math.PI * freqHz);
}
